import { isPaymentTermsDays } from './paymentTerms';
import type { Brand } from './types';

export const MAX_INVOICE_LOGO_BYTES = 200 * 1024;
export interface InvoicingBrand {
  business_name: string;
  tagline: string;
  logo_data_url: string;
  reply_to: string;
}
export interface InvoicingSettings {
  abn: string;
  bank_name: string;
  bsb: string;
  account_number: string;
  account_name: string;
  default_terms_days: number;
  accounts_email: string;
  footer_line: string;
  brands: Record<Brand, InvoicingBrand>;
}
export interface InvoiceBusiness {
  brand: Brand;
  name: string;
  tagline: string;
  logo_data_url: string;
  reply_to: string;
  email: string;
  abn: string;
  bank_name: string;
  bsb: string;
  account_number: string;
  account_name: string;
  terms_days: number;
  footer_line: string;
}
export function defaultInvoicingSettings(): InvoicingSettings {
  return { abn: '', bank_name: '', bsb: '', account_number: '', account_name: '',
    default_terms_days: 14, accounts_email: '', footer_line: 'Final files are released on payment',
    brands: { hsp: { business_name: 'Henry Sheahan Productions', tagline: '', logo_data_url: '', reply_to: '' },
      ss: { business_name: 'Sheahan Studios', tagline: '', logo_data_url: '', reply_to: '' } } };
}
function object(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}
const EMAIL = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;
/** Only self-contained image data is accepted, including for future browser PDF rendering. */
export function invoiceLogoError(value: string): string | null {
  if (!value) return null;
  const match = /^data:image\/(png|svg\+xml);base64,([A-Za-z0-9+/]*={0,2})$/.exec(value);
  if (!match || match[2].length % 4 !== 0) return 'Logo must be a PNG or SVG data URL.';
  if (match[2].length > Math.ceil(MAX_INVOICE_LOGO_BYTES / 3) * 4) return 'Logo must be 200 KB or smaller.';
  let data: string;
  try { data = atob(match[2]); } catch { return 'Invalid logo data.'; }
  if (data.length > MAX_INVOICE_LOGO_BYTES) return 'Logo must be 200 KB or smaller.';
  if (match[1] === 'png') {
    return data.startsWith('\x89PNG\r\n\x1a\n') ? null : 'Invalid PNG logo.';
  }
  // Reject active content, resource loads and XML entities. SVGs are used only as <img>.
  if (!/<svg(?:\s|>)/i.test(data) || !/<\/svg\s*>\s*$/i.test(data)
    || /<\s*\/?\s*(?:script|foreignObject|iframe|object|embed|image|use|style|animate\w*|set|a)(?:\s|>|\/)|<!DOCTYPE|<!ENTITY|\bon\w+\s*=|\b(?:href|src)\s*=|url\s*\(|@import|javascript:/i.test(data)) {
    return 'SVG logo must be self-contained and contain no scripts or external resources.';
  }
  return null;
}
export function parseInvoicingSettings(value: unknown): { settings: InvoicingSettings; error: string | null } {
  const settings = defaultInvoicingSettings();
  const input = object(value);
  if (!input) return { settings, error: 'Invoicing details must be an object.' };
  const read = (source: Record<string, unknown>, key: string, fallback: string, max = 200): string => {
    if (source[key] === undefined) return fallback;
    if (typeof source[key] !== 'string') throw new Error(`${key} must be text.`);
    const text = source[key].trim();
    if (text.length > max || /[\u0000-\u001f\u007f]/.test(text)) throw new Error(`${key} must be at most ${max} characters of single-line text.`);
    return text;
  };
  try {
    for (const key of ['abn', 'bank_name', 'bsb', 'account_number', 'account_name', 'accounts_email', 'footer_line'] as const) {
      settings[key] = read(input, key, settings[key]);
    }
    if (settings.abn && !/^(?:\d ?){10}\d$/.test(settings.abn)) throw new Error('ABN must contain eleven digits, with optional spaces.');
    if (settings.bsb && !/^\d{3}-?\d{3}$/.test(settings.bsb)) throw new Error('BSB must contain six digits, with an optional hyphen.');
    if (settings.account_number && !/^\d{1,20}$/.test(settings.account_number)) throw new Error('Account number must contain digits only, at most twenty.');
    if (settings.accounts_email && !EMAIL.test(settings.accounts_email)) throw new Error('Enter a valid accounts email.');
    if (input.default_terms_days !== undefined) {
      if (!isPaymentTermsDays(input.default_terms_days)) throw new Error('Default payment terms must be an integer from 0 to 365.');
      settings.default_terms_days = input.default_terms_days;
    }
    if (input.brands !== undefined) {
      const brands = object(input.brands);
      if (!brands) throw new Error('Brands must be an object.');
      for (const brand of ['hsp', 'ss'] as const) {
        if (brands[brand] === undefined) continue;
        const values = object(brands[brand]);
        if (!values) throw new Error(`${brand} details must be an object.`);
        const target = settings.brands[brand];
        for (const key of ['business_name', 'tagline', 'reply_to', 'logo_data_url'] as const) {
          target[key] = read(values, key, target[key], key === 'logo_data_url' ? Math.ceil(MAX_INVOICE_LOGO_BYTES / 3) * 4 + 100 : 200);
        }
        if (!target.business_name) throw new Error('Business name is required for each brand.');
        if (target.reply_to && !EMAIL.test(target.reply_to)) throw new Error(`Enter a valid ${brand} reply-to email.`);
        const logoError = invoiceLogoError(target.logo_data_url);
        if (logoError) throw new Error(logoError);
      }
    }
    return { settings, error: null };
  } catch (err) { return { settings, error: err instanceof Error ? err.message : 'Invalid invoicing details.' }; }
}
export function invoicingBusiness(settings: InvoicingSettings, brand: Brand, termsDays = settings.default_terms_days): InvoiceBusiness {
  const identity = settings.brands[brand];
  return { brand, name: identity.business_name, tagline: identity.tagline, logo_data_url: identity.logo_data_url,
    reply_to: identity.reply_to || settings.accounts_email, email: settings.accounts_email || identity.reply_to,
    abn: settings.abn, bank_name: settings.bank_name, bsb: settings.bsb, account_number: settings.account_number,
    account_name: settings.account_name, terms_days: termsDays, footer_line: settings.footer_line };
}
export function parseInvoiceBusinessSnapshot(raw: string | null | undefined): InvoiceBusiness | null {
  if (!raw) return null;
  try {
    const value = object(JSON.parse(raw));
    if (!value || (value.brand !== 'hsp' && value.brand !== 'ss') || typeof value.name !== 'string' || !value.name
      || !isPaymentTermsDays(value.terms_days)) return null;
    const fields = ['tagline', 'logo_data_url', 'reply_to', 'email', 'abn', 'bank_name', 'bsb', 'account_number', 'account_name', 'footer_line'];
    if (fields.some(key => typeof value[key] !== 'string') || invoiceLogoError(value.logo_data_url as string)) return null;
    // Return only the business fields. Pending-send ownership is internal metadata.
    return { brand: value.brand, name: value.name, terms_days: value.terms_days,
      tagline: value.tagline as string, logo_data_url: value.logo_data_url as string,
      reply_to: value.reply_to as string, email: value.email as string, abn: value.abn as string,
      bank_name: value.bank_name as string, bsb: value.bsb as string, account_number: value.account_number as string,
      account_name: value.account_name as string, footer_line: value.footer_line as string };
  } catch { return null; }
}
